import { computed, reactive, ref, unref, watch } from '@vue/composition-api'
import { Referral } from '@/api/model/referral'
import { Company } from '@/api/model/company'
import { Pipeline } from '@/api/model/pipeline'
import { useModal } from '@/composables/useModal'
import { useSteps } from '@/composables/useSteps'
import { v4 as uuid } from 'uuid'
import {
    AttachmentAPI,
    CommentAPI,
    CompanyAPI,
    ContactAPI,
    PipelineAPI,
    ProjectAPI,
    ReferralAPI,
    SourceAPI,
} from '@/api/backend-client'
import { Contact } from '@/api/model/contact'
import { User } from '@/api/model/user'
import { Project } from '@/api/model/project'
import { ReferralNode } from '@/api/model/referral-node'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import {
    BranchType,
    CommentOrigin,
    CommentVisibility,
    ReferralChannel,
    ReferralNodeType,
    ReferralStatus,
} from '@/api/types'

import { Branch } from '@/api/model/branch'
import { Source } from '@/api/model/source'
import { useCurrentData } from '@/composables/useCurrentData'
import { getAllowedUsers } from '@/helpers/security'
import { Comment } from '@/api/model/comment'
import { datetime } from '@/plugins/filters-date'
import i18n from '@/i18n'
import router from '@/router'
import Vue from 'vue'

const modal = reactive(useModal())
const steps = reactive(useSteps({ initialStep: 1, maxSteps: 4 }))
const { currentUser, currentCompany } = useCurrentData()

const projects = reactive<Project[]>([])
const outgoingNodes = reactive<ReferralNode[]>([])
const outgoingConnections = reactive<PipelineConnection[]>([])
const ownPipelines = reactive<Pipeline[]>([])

const isVisible = computed(() => modal.isVisible)
const usePipelines = computed(() => state.targetCompany && state.targetCompany.pipelinesEnabled)


const state = reactive({
    loading: ref<boolean>(true),
    submitting: ref<boolean>(false),
    targetCompany: ref<Company>(null),
    targetPipeline: ref<Pipeline>(),
    comment: ref<string>(null),
    commentVisibility: ref<CommentVisibility>(null),
    updateExistingContact: ref<boolean>(false),

    gdpr: ref<string>(null),
    gdprFile: ref<File>(null),

    followUp: reactive<{ type: string, informUsers: User[] }>({
        type: null,
        informUsers: [],
    }),

    referral: reactive(Referral.fromData({
        contact: Contact.fromData({

        }),
    })),
})

const referringToOwnCompany = computed(() => {
    if (!currentCompany.value) {
        return false
    }

    if (!state.referral.assignedToBranch) {
        return false
    }

    return currentCompany.value.branches
        .some(branch => branch.id === state.referral.assignedToBranch.id)
})

const availablePipelines = computed<Pipeline[]>(() => {
    if (!currentCompany.value) {
        return []
    }

    if (!state.targetCompany) {
        return []
    }

    // If referring to self, show all pipelines.
    if (state.targetCompany.id === currentCompany.value.id) {
        return (ownPipelines as Pipeline[])
            .filter(pipeline => pipeline.active)
    }

    // Build list of active pipelines.
    return outgoingConnections
        .filter((connection: PipelineConnection) => connection.sourceEnabled)
        .filter((connection: PipelineConnection) => connection.targetEnabled)
        .filter((connection: PipelineConnection) => connection.pipeline !== null)
        .filter((connection: PipelineConnection) => connection.targetCompany.id === state.targetCompany.id)
        .map((connection: PipelineConnection) => connection.pipeline)
        .filter(pipeline => pipeline.active)

        // Fix cyclic error, we keep targetCompany seperate anyway.
        .map((pipeline: Pipeline) => ({ ...pipeline, company: null } as Pipeline))
})

const availableReferralNodeTypes = computed<ReferralNodeType[]>(() => {
    if (!state.targetCompany) {
        return []
    }

    return outgoingNodes
        .filter((node: ReferralNode) => node.active)
        .filter((node: ReferralNode) => node.target.id === state.targetCompany.id)
        .map((node: ReferralNode) => node.type)
})


const availableCompanies = reactive<Company[]>([])
function updateAvailableCompanies(): void {
    const companies = new Map<string, Company>()

    outgoingNodes
        .filter((node: ReferralNode) => node.active)
        .forEach((node: ReferralNode) => companies.set(node.target.id, node.target))

    outgoingConnections
        .filter((connection: PipelineConnection) => connection.sourceEnabled)
        .filter((connection: PipelineConnection) => connection.targetEnabled)
        .filter((connection: PipelineConnection) => connection.pipeline !== null)
        .forEach((connection: PipelineConnection) => companies.set(connection.targetCompany.id, connection.targetCompany))

    availableCompanies.splice(0)
    availableCompanies.push(...Array.from(companies.values()))

    availableCompanies.sort((a, b) => a.name.localeCompare(b.name))

    // Insert current company if not already added
    if (currentCompany.value && !availableCompanies.some(company => company.id === currentCompany.value.id)) {
        availableCompanies.unshift(currentCompany.value)
    }
}

const availableSources = ref<Source[]>([])
async function updateAvailableSources(): Promise<void> {
    if (state.targetCompany === null) {
        availableSources.value.splice(0)
        return
    }

    const sources = (await SourceAPI.getAll({
        company: state.targetCompany.id,
        channel: ReferralChannel.USER,
        active: 1,
    }))

    availableSources.value.splice(0)
    availableSources.value.push(...sources)
}

const referringUsers = reactive<User[]>([])
function updateReferringUsers(): void {
    if (!currentUser.value || !currentCompany.value) {
        return
    }

    // If we're not referring from a branch yet, only allow picking own user.
    if (!state.referral.referredByBranch) {
        referringUsers.splice(0)
        referringUsers.push(currentUser.value)
        return
    }

    // Store previously selected user.
    const selectedUser = state.referral.referredBy

    // Reactive way to clear and fill array with available users.
    referringUsers.splice(0)
    referringUsers.push(...currentCompany.value.getAvailableUsers({
        branch: state.referral.referredByBranch as Branch,
    }))

    // Unset selected user if it does not exist in the new list of available users.
    if (selectedUser && referringUsers.some(user => user.id === selectedUser.id) === false) {
        state.referral.referredBy = null
    }
}

const referringBranches = reactive<Branch[]>([])
function updateReferringBranches(): void {
    if (!currentUser.value) {
        return
    }

    // Reactive way to clear and fill array.
    referringBranches.splice(0)
    referringBranches.push(...currentUser.value.branches
        .filter(branch => branch.type === BranchType.DEFAULT)
        .sort((a, b) => a.name.localeCompare(b.name)))
}

const assignableUsers = reactive<User[]>([])
function updateAssignableUsers(): void {
    if (state.targetCompany === null) {
        return
    }

    if (!state.referral.assignedToBranch) {
        return
    }

    assignableUsers.splice(0)
    assignableUsers.push(...state.targetCompany.getAvailableUsers({
        branch: state.referral.assignedToBranch as Branch,
    }))
}

const assignableBranches = reactive<Branch[]>([])
async function updateAssignableBranches(): Promise<void> {
    if (state.targetCompany === null) {
        return
    }

    // Clear existing branches.
    assignableBranches.splice(0)

    // Push legacy branches if pipelines is disabled.
    if (state.targetCompany.pipelinesEnabled === false) {
        assignableBranches.push(...state.targetCompany.getAvailableBranches()
            .filter(branch => branch.acceptsReferralType(state.referral.type)))
        assignableBranches.sort((a, b) => a.name.localeCompare(b.name))

        return
    }

    // Check if targetPipeline is set.
    if (!state.targetPipeline) {
        return
    }

    // Push available branches from target pipeline.
    assignableBranches.push(...state.targetPipeline.branches

        // targetPipeline.branches has no company set, so we need to set it manually.
        // this is used for security later to determine which users are available.
        .map(branch => Branch.fromData({ ...branch, company: state.targetCompany })))
    assignableBranches.sort((a, b) => a.name.localeCompare(b.name))
}

const informableUsers = reactive<User[]>([])
function updateInformableUsers(): void {
    if (state.targetCompany === null) {
        return
    }

    if (!state.referral.assignedToBranch) {
        return
    }

    informableUsers.splice(0)
    informableUsers.push(...state.targetCompany.getAvailableUsers()
        .filter(user => {

            // Can always inform a company manager.
            if (user.isCompanyManager) {
                return true
            }

            // If user is part of this branch, they can be informed.
            if (state.targetCompany.branches.some(branch => branch.id === state.referral.assignedToBranch.id)) {

                // Only if they are branch manager or team lead.
                return user.isBranchManager || user.isTeamLead
            }

            return false
        }))
}

const mentionableUsers = reactive<User[]>([])
async function updateMentionableUsers(): Promise<void> {
    mentionableUsers.splice(0)
    mentionableUsers.push(...await getAllowedUsers(
        state.referral.referredByBranch as Branch,
        unref(currentUser),
        state.referral.assignedToBranch as Branch,
        state.referral.assignedTo as User,
    ))
}

watch(() => [
    state.targetCompany,
], updateAvailableSources)

watch(() => [
    currentUser.value,
    currentCompany.value,
    state.referral.referredByBranch,
], updateReferringUsers)

watch(() => [
    currentUser.value,
    state.referral.type,
    usePipelines.value,
], updateReferringBranches)

watch(() => [
    state.targetCompany,
    state.referral.assignedToBranch,
], updateAssignableUsers)

watch(() => [
    state.targetCompany,
    state.targetPipeline,
    state.referral.type,
], updateAssignableBranches)

watch(() => [
    state.targetCompany,
    state.referral.assignedToBranch,
], updateInformableUsers)

watch(() => [
    state.referral.referredByBranch,
    state.referral.assignedToBranch,
    state.referral.assignedTo,
], updateMentionableUsers)

// Update state when inform type changes.
watch(() => state.followUp.type, () => {

    // Clear informable users when followup type if not inform.
    if (state.followUp.type !== 'inform') {
        state.followUp.informUsers.splice(0)
    }

    // Clear assignedTo if followup type is not assign.
    if (state.followUp.type !== 'assign') {
        state.referral.assignedTo = null
    }
})

// Auto select first company if there is only one option.
watch(availableCompanies, () => {
    if (availableCompanies.length === 1) {
        state.targetCompany = availableCompanies[0]
    }
})

// Auto select first referrable user if it's the only option.
watch(referringUsers, () => {
    if (referringUsers.length === 1) {
        state.referral.referredBy = referringUsers[0]
    }
})

// Auto select first referrable branch if it's the only option.
watch(referringBranches, () => {
    if (referringBranches.length === 1) {
        state.referral.referredByBranch = referringBranches[0]
    }
})

// Auto select first assignable branch if it's the only option.
watch(assignableBranches, () => {
    if (assignableBranches.length === 1) {
        state.referral.assignedToBranch = assignableBranches[0]
    }
})


/**
 * Load data for lead form.
 * (splice/push is reactive, rather than overwriting the array)
 */
async function loadData(): Promise<void> {
    state.loading = true

    projects.splice(0)
    projects.push(...await ProjectAPI.getAll({ company: currentCompany.value.id }))

    outgoingNodes.splice(0)
    outgoingNodes.push(...await CompanyAPI.getOutgoingReferrals())

    outgoingConnections.splice(0)
    outgoingConnections.push(...await CompanyAPI.getOutgoingConnections())

    updateAvailableCompanies()

    ownPipelines.splice(0)
    ownPipelines.push(...await PipelineAPI.getAll({ company: currentCompany.value.id }))

    state.loading = false
}

/**
 * Called when opening lead form.
 */
async function open(): Promise<void> {
    reset()
    modal.open()
    await loadData()
}

/**
 * Called when closing lead form.
 */
function close(): void {
    modal.close()
}

/**
 * Reset form to initial state.
 */
function reset(): void {
    steps.reset()

    state.referral = reactive(Referral.fromData({
        id: uuid(),
        contact: Contact.fromData({
            id: uuid(),
            gender: 'unknown',
        }),
        referredBy: currentUser.value,
        referredByBranch: currentUser.value?.branches.length === 1 ? currentUser.value.branches[0] : null,
        score: 0,
        newSource: null,
        status: 'new',
    }))

    state.followUp = reactive<{ type: string, informUsers: User[] }>({
        type: null,
        informUsers: [],
    })

    state.comment = null
    state.commentVisibility = currentCompany.value?.defaultCommentVisibility || null
    state.updateExistingContact = false
    state.targetCompany = null
    state.targetPipeline = null
    state.gdpr = null
    state.gdprFile = null
}

/**
 * Called when submitting lead form.
 */
async function submit(): Promise<void> {
    state.submitting = true

    try {
        // If we selected an existing contact, update that seperately.
        if (state.updateExistingContact) {
            await ContactAPI.update(
                state.referral.contact.id,
                state.referral.contact,
            )

        } else {
            await ContactAPI.create({
                ...state.referral.contact,
                company: currentCompany.value.resourceIdentifier as unknown as Company,
                newSource: state.referral.newSource.resourceIdentifier as unknown as Source,
            } as Contact)
        }

        // Manage referral values when pipelines are used.
        if (usePipelines.value) {
            state.referral.status = ReferralStatus.OPEN
            state.referral.pipeline = state.targetPipeline
            // @todo: fix wrong state being assigned by ReferralBuilder -> backend

        // @todo: remove this when pipelines are used everywhere.
        } else {
            state.referral.status = ReferralStatus.NEW
            state.referral.pipeline = null
        }

        // Create new referral.
        const newReferral = await ReferralAPI.create({
            ...state.referral,
            contact: state.referral.contact?.resourceIdentifier || null,
            pipeline: state.referral.pipeline?.resourceIdentifier || null,
            referredBy: state.referral.referredBy?.resourceIdentifier || null,
            referredByBranch: state.referral.referredByBranch?.resourceIdentifier || null,
            assignedTo: state.referral.assignedTo?.resourceIdentifier || null,
            assignedToBranch: state.referral.assignedToBranch?.resourceIdentifier || null,
            newSource: state.referral.newSource?.resourceIdentifier || null,
        } as unknown as Referral)

        // Helper to create comment.
        const createComment = async (referral: Referral, comment: string, user: User|null = null, origin: CommentOrigin = null, visibility: CommentVisibility = CommentVisibility.PUBLIC) => {
            await CommentAPI.create(Comment.fromData({
                referral: referral.resourceIdentifier,
                user: user ? user.resourceIdentifier : null,
                id: uuid(),
                comment,
                origin,
                visibility,
            }))
        }

        // Helper to create GDPR comment.
        const createGdprComment = async (referral: Referral, commentTranslationKey: string) => {
            await createComment(
                newReferral,
                i18n.t(commentTranslationKey, {
                    timestamp: datetime(new Date()),
                    userName: currentUser.value.name,
                    assignedToBranchName: newReferral.assignedToBranch.name,
                    assignedToCompanyName: newReferral.assignedToBranch.company.name,
                }).toString(),
            )
        }

        // When followup type is assign, we inform assigned user if assigned user is someone else
        if (state.followUp.type === 'assign' && state.referral.assignedTo.id !== currentUser.value.id) {
            await ReferralAPI.notify(
                newReferral.id,
                'assignedUser',
                [],
            )
        }

        // When followup type is inform, we inform all selected users.
        if (state.followUp.type === 'inform') {
            const targetIds = state.followUp.informUsers.map(user => user.id)
            await ReferralAPI.notify(
                newReferral.id,
                'branchManagers', // the API expects 'branchManagers' but any user can be a target
                targetIds,
            )
        }

        // When creating a lead for someone else, we place a comment stating this.
        if (state.referral.referredBy.id !== currentUser.value.id) {
            await createComment(
                newReferral,
                i18n.t('forms.create-task-form.referringUserDifferentComment', {
                    referredByUserName: newReferral.referredBy.name,
                    userName: currentUser.value.name,
                }).toString(),
            )
        }

        // Place additional comment when source is sitekick.
        if (state.referral.newSource?.name?.toLowerCase() === 'sitekick') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.verbal-comment-sitekick',
            )

        // We place an additional comment when GDPR approval is verbal.
        } else if (state.gdpr === 'verbal') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.verbal-comment',
            )

        // For written approval we store the attached file.
        } else if (state.gdpr === 'written') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.written-comment',
            )

            await AttachmentAPI.createWithFile(
                newReferral,
                uuid(),
                state.gdprFile,
            )
        }

        // Create entered comment.
        if (state.comment?.length > 0) {
            await createComment(newReferral, state.comment, newReferral.referredBy, CommentOrigin.USER, state.commentVisibility)
        }

        // Refresh referral
        await ReferralAPI.get(newReferral.id, true)

        // Close modal.
        close()

        // Mark referral created so we can show this on the detail page.
        window.keyvue.referralCreated = true

        // Push detail route.
        await router.push({
            name: 'tasks_detail',
            params: {
                referralId: newReferral.id,
                contactId: newReferral.contact.id,
                toOne: this.$currentUser.betaOptInEnabled,
            },
        })

    } catch (error) {
        Vue.notify({
            type: 'error',
            title: 'Foutmelding',
            text: error.message,
        })

    } finally {
        state.submitting = false
    }
}

export function useCreateLeadForm() {
    return {
        state,
        steps,

        open,
        close,
        submit,

        isVisible,
        usePipelines,
        referringToOwnCompany,
        availableCompanies,
        availableSources,
        availablePipelines,
        availableReferralNodeTypes,

        referringUsers,
        assignableUsers,
        referringBranches,
        assignableBranches,
        informableUsers,
        mentionableUsers,

        projects,
    }
}
